import React, { useState, useContext, useEffect, useCallback } from 'react';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { useDialog } from '../components/ConfirmProvider';
import { formatDateTime } from '../utils/dateFormat';
import {
  FaInfoCircle, FaCog, FaPlus, FaTrash, FaEdit,
  FaPlay, FaFilter, FaHistory,
  FaToggleOn, FaToggleOff
} from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';   // shared design system

const LIMIT_TYPES = [
  { v: 'per_tx_max',         label: 'Per-tx maximum',
    hint: 'Block transactions whose single amount meets or exceeds the threshold.' },
  { v: 'per_tx_min',         label: 'Per-tx minimum',
    hint: 'Block transactions whose amount is below the threshold.' },
  { v: 'cumulative_value',   label: 'Cumulative value (window)',
    hint: 'Sum tx amounts over window_hours per customer; block when projected sum >= threshold.' },
  { v: 'cumulative_count',   label: 'Cumulative count (window)',
    hint: 'Count tx over window_hours per customer; block when projected count > threshold.' },
  { v: 'time_of_day_block',  label: 'Time-of-day block',
    hint: 'Reject transactions that fall between time_start and time_end (24h, wraps midnight).' },
  { v: 'balance_max',        label: 'Wallet balance cap',
    hint: 'Block transactions that would push the projected 90-day net wallet balance above the threshold.' }
];

const CUSTOMER_TYPES = ['individual','sme','agent','corporate','distribution','acceptor'];
const KYC_TIERS      = ['lite','medium','full'];
const RISK_TIERS     = ['low','medium','high'];
const DIRECTIONS     = ['credit','debit','transfer_in','transfer_out'];
const ENFORCEMENTS   = ['block','warn'];

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

const blank = () => ({
  organisation_id: '', name: '', description: '',
  customer_type: '', kyc_tier: '', risk_tier: '',
  channel: '', direction: '', customer_id: '',
  min_risk_score: '', max_risk_score: '',
  limit_type: 'per_tx_max',
  threshold: '', currency: 'NAD',
  window_hours: '', time_start: '', time_end: '',
  enforcement: 'block', soft_threshold_pct: '',
  priority: 100, is_active: true, notes: ''
});

// Strip empty strings from the form data before posting so the backend zod
// schemas (which expect optional NULLs) don't trip on "".
function normalise(d) {
  const out = {};
  for (const [k, v] of Object.entries(d)) {
    if (v === '' || v === null || v === undefined) continue;
    out[k] = v;
  }
  // booleans must remain
  out.is_active = !!d.is_active;
  return out;
}

function ManageLimitRules() {
  const { user } = useContext(UserContext);
  const { confirm } = useDialog();
  const canRead     = hasPermission(user, 'limits.read');
  const canCreate   = hasPermission(user, 'limits.create');
  const canUpdate   = hasPermission(user, 'limits.update');
  const canToggle   = hasPermission(user, 'limits.toggle');
  const canDelete   = hasPermission(user, 'limits.delete');
  const canSimulate = hasPermission(user, 'limits.simulate');

  const [rules, setRules]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [editor, setEditor]   = useState(null);
  const [busy, setBusy]       = useState(false);

  // Simulator state
  const [showSim, setShowSim]     = useState(false);
  const [simData, setSimData]     = useState({ customer_id: '', amount: '', direction: 'debit', channel: '' });
  const [simResult, setSimResult] = useState(null);
  const [simBusy, setSimBusy]     = useState(false);

  // Audit log state
  const [showLog, setShowLog]   = useState(false);
  const [logRows, setLogRows]   = useState([]);
  const [logFilter, setLogFilter] = useState({ outcome: '', customer_id: '' });
  const [logLoading, setLogLoading] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get('/limit-rules');
      setRules(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load limit rules');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = { limit: 200 };
      if (logFilter.outcome) params.outcome = logFilter.outcome;
      if (logFilter.customer_id) params.customer_id = logFilter.customer_id;
      const r = await axios.get('/limit-rules/evaluations', { params });
      setLogRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load evaluation log');
    } finally {
      setLogLoading(false);
    }
  }, [logFilter]);

  useEffect(() => { if (canRead) fetchRules(); }, [canRead, fetchRules]);
  useEffect(() => { if (showLog) fetchLog(); }, [showLog, fetchLog]);

  const openCreate = () => setEditor({ mode: 'create', data: blank() });
  const openEdit = (rule) => setEditor({
    mode: 'edit',
    existingId: rule.id,
    data: {
      organisation_id: rule.organisation_id || '',
      name: rule.name || '', description: rule.description || '',
      customer_type: rule.customer_type || '',
      kyc_tier: rule.kyc_tier || '',
      risk_tier: rule.risk_tier || '',
      channel: rule.channel || '',
      direction: rule.direction || '',
      customer_id: rule.customer_id || '',
      min_risk_score: rule.min_risk_score ?? '',
      max_risk_score: rule.max_risk_score ?? '',
      limit_type: rule.limit_type,
      threshold: rule.threshold,
      currency: rule.currency || 'NAD',
      window_hours: rule.window_hours ?? '',
      time_start: rule.time_start || '',
      time_end: rule.time_end || '',
      enforcement: rule.enforcement || 'block',
      soft_threshold_pct: rule.soft_threshold_pct ?? '',
      priority: rule.priority ?? 100,
      is_active: !!rule.is_active,
      notes: rule.notes || ''
    }
  });

  const submit = async () => {
    if (!editor) return;
    setBusy(true); setError(null);
    try {
      const body = normalise(editor.data);
      if (editor.mode === 'create') {
        await axios.post('/limit-rules', body);
      } else {
        await axios.patch(`/limit-rules/${editor.existingId}`, body);
      }
      setEditor(null);
      fetchRules();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleRule = async (id) => {
    try {
      await axios.patch(`/limit-rules/${id}/toggle`);
      fetchRules();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Toggle failed');
    }
  };

  const deleteRule = async (rule) => {
    const ok = await confirm({
      title: 'Delete limit rule',
      message: `Delete "${rule.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await axios.delete(`/limit-rules/${rule.id}`);
      fetchRules();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Delete failed');
    }
  };

  const runSimulation = async () => {
    setSimBusy(true); setSimResult(null);
    try {
      const r = await axios.post('/limit-rules/simulate', {
        customer_id: Number(simData.customer_id),
        amount: Number(simData.amount),
        direction: simData.direction,
        channel: simData.channel || undefined
      });
      setSimResult(r.data);
    } catch (e) {
      setSimResult({ error: e.response?.data?.error?.message || e.message || 'Simulation failed' });
    } finally {
      setSimBusy(false);
    }
  };

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to view limit rules.
            Ask your administrator for the <code>limits.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  const globalRules = rules.filter(r => !r.organisation_id);
  const orgRules    = rules.filter(r =>  r.organisation_id);
  const activeCount = rules.filter(r => r.is_active).length;
  const breachCount = rules.reduce((s, r) => s + (Number(r.breaches_30d) || 0), 0);

  return (
    <div className="page-container">
      {/* Stats */}
      <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
        <div className="stats-grid">
          <div className="stat-card stat-card--teal">
            <div className="stat-label">Total rules</div>
            <div className="stat-value stat-value--teal">{rules.length}</div>
          </div>
          <div className="stat-card stat-card--blue">
            <div className="stat-label">Active</div>
            <div className="stat-value stat-value--blue">{activeCount}</div>
          </div>
          <div className="stat-card stat-card--orange">
            <div className="stat-label">Disabled</div>
            <div className="stat-value stat-value--orange">{rules.length - activeCount}</div>
          </div>
          <div className="stat-card stat-card--brown">
            <div className="stat-label">Breaches (30d)</div>
            <div className="stat-value">{breachCount}</div>
          </div>
        </div>
      </div>
        </div>

      {/* Header + actions */}
      <section className="form-section" style={{ marginBottom: '1rem' }}>
        <header className="form-section__head">
          <h4 className="form-section__title">Limits &amp; Risk Control</h4>
          <p className="form-section__subtitle">
            MTC25-26-O §24-§27 — per-tx, cumulative, time-of-day, balance, and risk-tier limits.
            Global rules apply to every organisation; org-scoped rules add to (or override) defaults.
          </p>
        </header>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {canCreate && (
            <button className="export-button" onClick={openCreate}>
              <FaPlus /> New limit
            </button>
          )}
          {canSimulate && (
            <button className="btn-cancel" onClick={() => setShowSim(s => !s)}>
              <FaPlay /> {showSim ? 'Hide simulator' : 'Simulator'}
            </button>
          )}
          <button className="btn-cancel" onClick={() => setShowLog(s => !s)}>
            <FaHistory /> {showLog ? 'Hide log' : 'Audit log'}
          </button>
        </div>
      </section>

      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {/* Simulator panel */}
      {showSim && canSimulate && (
        <section className="form-section" style={{ marginBottom: '1rem' }}>
          <header className="form-section__head">
            <h4 className="form-section__title">Limit simulator</h4>
            <p className="form-section__subtitle">
              Evaluate a candidate transaction against the active rule set without persisting anything.
            </p>
          </header>
          <div className="form-grid-2">
            <div className="form-group">
              <label>Customer ID</label>
              <input type="number" value={simData.customer_id}
                     onChange={e => setSimData(s => ({ ...s, customer_id: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Amount</label>
              <input type="number" step="0.01" value={simData.amount}
                     onChange={e => setSimData(s => ({ ...s, amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Direction</label>
              <select value={simData.direction}
                      onChange={e => setSimData(s => ({ ...s, direction: e.target.value }))}>
                {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Channel (optional)</label>
              <input type="text" value={simData.channel}
                     onChange={e => setSimData(s => ({ ...s, channel: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.4rem' }}>
            <button className="export-button" disabled={simBusy || !simData.customer_id || !simData.amount}
                    onClick={runSimulation}>
              {simBusy ? 'Running…' : 'Run simulation'}
            </button>
          </div>

          {simResult && (
            <div style={{ marginTop: '0.8rem' }}>
              {simResult.error
                ? <div className="alert-banner alert-banner--danger"><span className="alert-banner__body">{simResult.error}</span></div>
                : (
                  <>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', textTransform: 'uppercase',
                                     letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Decision</span>
                      <span className={`tm-pill ${
                        simResult.decision === 'hard_breach' ? 'tm-pill--sev-critical' :
                        simResult.decision === 'soft_breach' ? 'tm-pill--sev-medium'   :
                        'tm-pill--sev-low'
                      }`}>{simResult.decision}</span>
                    </div>
                    {simResult.evaluations.length === 0 && (
                      <p style={{ color: 'var(--text-secondary)' }}>No rules matched this candidate.</p>
                    )}
                    {simResult.evaluations.map(ev => (
                      <div key={ev.rule_id} style={{
                        background: 'var(--card-bg,#fff)',
                        border: '1px solid var(--card-border,#e3e6ea)',
                        borderLeft: `4px solid ${ev.outcome === 'hard_breach' ? '#b71c1c' : ev.outcome === 'soft_breach' ? '#ef6c00' : '#2e7d32'}`,
                        padding: '0.6rem 0.9rem',
                        marginBottom: '0.4rem',
                        fontSize: '0.85rem'
                      }}>
                        <div style={{ fontWeight: 600 }}>{ev.rule_name}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                          {ev.limit_type} · {ev.enforcement} · threshold {Number(ev.threshold).toLocaleString()}
                        </div>
                        {ev.message && <div style={{ marginTop: '0.25rem' }}>{ev.message}</div>}
                      </div>
                    ))}
                  </>
                )}
            </div>
          )}
        </section>
      )}

      {/* Audit log */}
      {showLog && (
        <section className="form-section" style={{ marginBottom: '1rem' }}>
          <header className="form-section__head">
            <h4 className="form-section__title">Evaluation log</h4>
            <p className="form-section__subtitle">
              Append-only audit of every rule check — allowed, soft-breached, and blocked.
            </p>
          </header>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            <div className="form-group" style={{ minWidth: 160 }}>
              <label><FaFilter /> Outcome</label>
              <select value={logFilter.outcome}
                      onChange={e => setLogFilter(f => ({ ...f, outcome: e.target.value }))}>
                <option value="">all</option>
                <option value="allow">allow</option>
                <option value="soft_breach">soft_breach</option>
                <option value="hard_breach">hard_breach</option>
              </select>
            </div>
            <div className="form-group" style={{ minWidth: 160 }}>
              <label>Customer ID</label>
              <input type="number" value={logFilter.customer_id}
                     onChange={e => setLogFilter(f => ({ ...f, customer_id: e.target.value }))} />
            </div>
            <div className="form-group" style={{ alignSelf: 'end' }}>
              <button className="export-button" onClick={fetchLog} disabled={logLoading}>
                {logLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>At</th>
                  <th>Outcome</th>
                  <th>Rule</th>
                  <th>Type</th>
                  <th>Customer</th>
                  <th>Tx</th>
                  <th>Attempted</th>
                  <th>Cumulative</th>
                  <th>Threshold</th>
                </tr>
              </thead>
              <tbody>
                {logRows.length === 0 && (
                  <tr><td colSpan={9} style={{ color: 'var(--text-secondary)', padding: '0.8rem' }}>No evaluations yet.</td></tr>
                )}
                {logRows.map(row => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.evaluated_at)}</td>
                    <td>
                      <span className={`tm-pill ${
                        row.outcome === 'hard_breach' ? 'tm-pill--sev-critical' :
                        row.outcome === 'soft_breach' ? 'tm-pill--sev-medium'   :
                        'tm-pill--sev-low'
                      }`}>{row.outcome}</span>
                    </td>
                    <td>{row.rule_name || `#${row.rule_id}`}</td>
                    <td>{row.limit_type || '—'}</td>
                    <td>{row.customer_id}</td>
                    <td>{row.transaction_id || '—'}</td>
                    <td>{row.attempted_value != null ? Number(row.attempted_value).toLocaleString() : '—'}</td>
                    <td>{row.cumulative_value != null ? Number(row.cumulative_value).toLocaleString() : '—'}</td>
                    <td>{row.threshold != null ? Number(row.threshold).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {loading && <p>Loading…</p>}
      {!loading && rules.length === 0 && (
        <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No limit rules configured.</p>
      )}

      {[['Global limits', globalRules], ['Organisation limits', orgRules]].map(([title, list]) => (
        list.length > 0 && (
          <div key={title} style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em',
                         color: 'var(--text-secondary)', margin: '0 0 0.6rem' }}>{title}</h3>
            {list.map(rule => (
              <div key={rule.id}
                   style={{
                     background: 'var(--card-bg,#fff)',
                     border: '1px solid var(--card-border,#e3e6ea)',
                     borderLeft: `4px solid ${
                       rule.enforcement === 'warn' ? '#ef6c00' :
                       rule.is_active ? 'var(--accent,#00796b)' : 'var(--text-muted,#90a4ae)'
                     }`,
                     padding: '0.9rem 1.1rem',
                     marginBottom: '0.5rem',
                     opacity: rule.is_active ? 1 : 0.7
                   }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                                  color: 'var(--text-secondary)', fontWeight: 700 }}>
                      {rule.limit_type} · {rule.enforcement} · priority {rule.priority}
                    </div>
                    <h4 style={{ margin: '0.2rem 0 0.3rem' }}>{rule.name}</h4>
                    {rule.description && <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{rule.description}</p>}
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span className="tm-pill">
                        {Number(rule.threshold).toLocaleString()} {rule.currency}
                      </span>
                      {rule.window_hours && <span className="tm-pill">window {rule.window_hours}h</span>}
                      {rule.customer_type && <span className="tm-pill">customer:{rule.customer_type}</span>}
                      {rule.kyc_tier && <span className="tm-pill">kyc:{rule.kyc_tier}</span>}
                      {rule.risk_tier && <span className="tm-pill">risk:{rule.risk_tier}</span>}
                      {rule.channel && <span className="tm-pill">ch:{rule.channel}</span>}
                      {rule.direction && <span className="tm-pill">dir:{rule.direction}</span>}
                      {rule.soft_threshold_pct != null && <span className="tm-pill">soft@{rule.soft_threshold_pct}%</span>}
                      {rule.organisation_name && <span className="tm-pill">{rule.organisation_name}</span>}
                      {(rule.breaches_30d || 0) > 0 && (
                        <span className="tm-pill tm-pill--sev-medium">{rule.breaches_30d} breach(es) 30d</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    {canToggle && (
                      <button className="action-button" onClick={() => toggleRule(rule.id)}
                              title={rule.is_active ? 'Disable' : 'Enable'}>
                        {rule.is_active ? <FaToggleOn className="toggle-icon--on" /> : <FaToggleOff className="toggle-icon--off" />}
                      </button>
                    )}
                    {canUpdate && (
                      <button className="action-button" onClick={() => openEdit(rule)}
                              title="Edit">
                        <FaEdit />
                      </button>
                    )}
                    {canDelete && (
                      <button className="action-button delete" onClick={() => deleteRule(rule)}
                              title="Delete">
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ))}

      {/* Create / edit modal */}
      {editor && (
        <div className="modal-overlay" onClick={() => !busy && setEditor(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <button className="modal-close" onClick={() => !busy && setEditor(null)}>×</button>
            <h2 style={{ marginTop: 0 }}>
              <FaCog style={{ marginRight: '0.4rem' }} />
              {editor.mode === 'create' ? 'New limit rule' : `Edit limit "${editor.data.name}"`}
            </h2>

            <div className="form-grid-2">
              <div className="form-group form-group--full">
                <label>Name</label>
                <input type="text" value={editor.data.name}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, name: e.target.value } }))} />
              </div>
              <div className="form-group form-group--full">
                <label>Description</label>
                <textarea rows={2} value={editor.data.description}
                          onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, description: e.target.value } }))} />
              </div>

              <div className="form-group">
                <label>Limit type</label>
                <select value={editor.data.limit_type}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, limit_type: e.target.value } }))}>
                  {LIMIT_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  {LIMIT_TYPES.find(t => t.v === editor.data.limit_type)?.hint}
                </p>
              </div>
              <div className="form-group">
                <label>Enforcement</label>
                <select value={editor.data.enforcement}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, enforcement: e.target.value } }))}>
                  {ENFORCEMENTS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Threshold</label>
                <input type="number" step="0.01" value={editor.data.threshold}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, threshold: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>Currency</label>
                <input type="text" value={editor.data.currency}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, currency: e.target.value.toUpperCase() } }))} />
              </div>

              <div className="form-group">
                <label>Window (hours)</label>
                <input type="number" value={editor.data.window_hours}
                       placeholder="cumulative_* only"
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, window_hours: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>Soft threshold %</label>
                <input type="number" min="1" max="100" value={editor.data.soft_threshold_pct}
                       placeholder="e.g. 90"
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, soft_threshold_pct: e.target.value } }))} />
              </div>

              {editor.data.limit_type === 'time_of_day_block' && (
                <>
                  <div className="form-group">
                    <label>Start (HH:MM)</label>
                    <input type="time" value={editor.data.time_start}
                           onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, time_start: e.target.value } }))} />
                  </div>
                  <div className="form-group">
                    <label>End (HH:MM)</label>
                    <input type="time" value={editor.data.time_end}
                           onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, time_end: e.target.value } }))} />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Customer type</label>
                <select value={editor.data.customer_type}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, customer_type: e.target.value } }))}>
                  <option value="">(any)</option>
                  {CUSTOMER_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>KYC tier</label>
                <select value={editor.data.kyc_tier}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, kyc_tier: e.target.value } }))}>
                  <option value="">(any)</option>
                  {KYC_TIERS.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Risk tier</label>
                <select value={editor.data.risk_tier}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, risk_tier: e.target.value } }))}>
                  <option value="">(any)</option>
                  {RISK_TIERS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Direction</label>
                <select value={editor.data.direction}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, direction: e.target.value } }))}>
                  <option value="">(any)</option>
                  {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Channel</label>
                <input type="text" value={editor.data.channel}
                       placeholder="e.g. cash, eft, mobile_money"
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, channel: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>Customer ID (single-customer override)</label>
                <input type="number" value={editor.data.customer_id}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, customer_id: e.target.value } }))} />
              </div>

              <div className="form-group">
                <label>Min risk score</label>
                <input type="number" step="0.01" min="0" max="99.99" value={editor.data.min_risk_score}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, min_risk_score: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>Max risk score</label>
                <input type="number" step="0.01" min="0" max="99.99" value={editor.data.max_risk_score}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, max_risk_score: e.target.value } }))} />
              </div>

              <div className="form-group">
                <label>Priority (lower = higher precedence)</label>
                <input type="number" min="1" max="10000" value={editor.data.priority}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, priority: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>&nbsp;</label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                               fontWeight: 600, textTransform: 'none' }}>
                  <input type="checkbox" checked={editor.data.is_active}
                         onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, is_active: e.target.checked } }))} />
                  Active
                </label>
              </div>
              <div className="form-group form-group--full">
                <label>Notes</label>
                <textarea rows={2} value={editor.data.notes}
                          onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, notes: e.target.value } }))} />
              </div>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" disabled={busy} onClick={() => setEditor(null)}>Cancel</button>
              <button className="export-button" disabled={busy} onClick={submit}>
                {busy ? 'Saving…' : editor.mode === 'create' ? 'Create rule' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageLimitRules;
